import { useEffect, useMemo, useState } from "react";

import type { AdminApi } from "@/api";
import { TONE_CLASS } from "@/components/batch-test/batchCells";
import { modelLabel, shortSha, specKey, teLetters, teName, teSub } from "@/components/batch-test/teSpec";
import { TraceMetricsRow, TraceView } from "@/components/llm-trace";
import { Badge, Banner, Card, CardHead, Disclosure, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { BatchPageDetail, BatchResultDetail, TeBaseline } from "@/types";
import { evaluationOutcomeFrom } from "@/utils/evaluation";
import { compactTime } from "@/utils/format";
import { formatScore, incomparableReason, incomparableTag, isComparable } from "@/utils/rubric";
import { fromReplaySnapshot } from "@/utils/trace/normalize";
import { CriteriaCard, FinalFormula, GroupTable, scoreText, V1Axes } from "./JudgeDetail";
import { readJudgement } from "./judgement";
import { tcShort } from "./MatrixTable";
import { BackLink } from "./BackLink";
import { formatLatency, replayLatencyMs } from "./latency";
import { scoreColor } from "./scoreColor";

function errorText(err: unknown) {
  return err instanceof Error ? err.message : String(err);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * What a cell is, said once, for the header and the explanation under it.
 * Never a 0 where there is no score (C5) — a dash and the reason.
 */
type ResultState =
  /** `tag` names the rubric/policy of a score that is not compared (R10). */
  | { kind: "scored"; score: number; tag: string | null; reason: string | null }
  | { kind: "no_reference" }
  | { kind: "not_scored_yet" }
  | { kind: "judge_failed"; reason: string | null }
  | { kind: "failed"; reason: string | null }
  | { kind: "skipped"; reason: string | null }
  | { kind: "in_flight"; status: string };

function resultState(detail: BatchResultDetail): ResultState {
  const { item } = detail;
  if (item.status === "pending" || item.status === "running") return { kind: "in_flight", status: item.status };
  if (item.status === "skipped") return { kind: "skipped", reason: item.error };
  if (item.status === "failed") return { kind: "failed", reason: item.error };
  const outcome = evaluationOutcomeFrom(detail.evaluation);
  switch (outcome.status) {
    case "scored":
      return isComparable(outcome)
        ? { kind: "scored", score: outcome.score, tag: null, reason: null }
        : {
            kind: "scored",
            score: outcome.score,
            tag: incomparableTag(outcome),
            reason: incomparableReason(outcome),
          };
    case "failed":
      return { kind: "judge_failed", reason: outcome.reason };
    case "not_evaluable":
      return outcome.reason === "not_scored_yet" ? { kind: "not_scored_yet" } : { kind: "no_reference" };
    default:
      return { kind: "not_scored_yet" };
  }
}

const TAG: Record<ResultState["kind"], string> = {
  scored: "scored",
  no_reference: "not evaluable",
  not_scored_yet: "채점 대기",
  judge_failed: "채점 실패",
  failed: "failed",
  skipped: "제외",
  in_flight: "실행 중",
};

function explanation(state: ResultState, detail: BatchResultDetail): string | null {
  switch (state.kind) {
    case "scored":
      return state.reason;
    case "no_reference":
      return "이 TC 에 검토에서 통과한 Reference 가 없습니다. 0 점도 실패도 아니며 추이 집계에서 빠집니다. 답변은 기록됐습니다.";
    case "not_scored_yet":
      return detail.active_reference
        ? `아직 채점하지 않았습니다. Reference v${detail.active_reference.version} 이 있으므로 다음 채점에서 점수가 납니다.`
        : "아직 채점하지 않았고, 이 TC 에는 Reference 가 없습니다 — 지금 채점하면 평가 불가가 됩니다. Reference 생성이 먼저입니다.";
    case "judge_failed":
      return `Judge 가 점수를 내지 못했습니다${state.reason ? ` — ${state.reason}` : ""}.`;
    case "failed":
      return `실행이 답변에 도달하지 못했습니다${state.reason ? ` — ${state.reason}` : ""}.`;
    case "skipped":
      return `이 실행에서 제외됐습니다${state.reason ? ` — ${state.reason}` : ""}.`;
    case "in_flight":
      return "아직 실행 중입니다. 진행은 Batch Test 에서 볼 수 있습니다.";
  }
}

/**
 * Test Result — one TC under one run (fe/TEST_CASES_RESULT.md §2~§5).
 *
 * The address is the run and the TC: a cell is always exactly one run, and
 * a run outlives its page (SET NULL). The page is read only to name the TE
 * the way the Matrix does (TE-A, TE-B …) — letters are the page's.
 *
 * The trace comes from the saved snapshot, never a live lookup: batch
 * replay sessions are deleted after the run (§1). It goes through the same
 * normalizer Negative Reviews uses (`fromReplaySnapshot`) and the same
 * renderer Conversations uses (`TraceView`).
 */
export function ResultView({
  api,
  runId,
  testCaseId,
  reloadKey = 0,
}: {
  api: AdminApi;
  runId: string;
  testCaseId: string;
  reloadKey?: number;
}) {
  const [detail, setDetail] = useState<BatchResultDetail | null>(null);
  const [page, setPage] = useState<BatchPageDetail | null>(null);
  const [baseline, setBaseline] = useState<TeBaseline | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const found = await api.getBatchResult(runId, testCaseId);
        if (!alive) return;
        setDetail(found);
        const [pageDetail, base] = await Promise.all([
          found.run.page_id ? api.getBatchPage(found.run.page_id).catch(() => null) : null,
          api.getTeBaseline().catch(() => null),
        ]);
        if (!alive) return;
        setPage(pageDetail);
        setBaseline(base);
      } catch (err) {
        if (alive) setError(errorText(err));
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, runId, testCaseId, reloadKey]);

  const trace = useMemo(
    () => (detail?.replay ? fromReplaySnapshot(detail.replay) : null),
    [detail],
  );

  if (error) {
    return (
      <div className="grid gap-4">
        <BackLink pageId={null} label={null} />
        <Banner tone="error">{error}</Banner>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="grid gap-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }

  const { run, item, replay, reference } = detail;
  const state = resultState(detail);
  const letter = page ? (teLetters(page.runs).get(specKey(run.te_spec ?? {})) ?? "TE-?") : "TE";
  const label = detail.page?.label ?? page?.label ?? null;
  const query =
    text(isRecord(replay?.trigger_message) ? replay.trigger_message.content : "") ||
    detail.test_case?.trigger_preview ||
    detail.test_case?.title ||
    "—";
  const manifest = isRecord(replay?.historical_replay) ? replay.historical_replay : null;
  const code = isRecord(manifest?.code) ? manifest.code : null;
  const judgement = readJudgement(replay?.criteria_evaluation);
  const v2 = judgement?.shape === "v2" ? judgement : null;
  const v1 = judgement?.shape === "v1" ? judgement : null;
  const rubric = judgement?.rubricVersion ?? rubricOf(detail.evaluation);
  const why = explanation(state, detail);
  const runLabel = `${label ?? "Batch"} · ${letter} 실행 · ${compactTime(run.created_at)}`;

  return (
    <div className="grid gap-4">
      <BackLink pageId={detail.page?.page_id ?? run.page_id} label={label} />

      {/* Score header (§3) */}
      <Card className="flex flex-wrap items-center gap-4 px-4 py-4">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-semibold tabular-nums">
            {state.kind === "scored" ? formatScore(state.score) : "—"}
          </span>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[11px]",
              state.kind === "scored"
                ? state.tag
                  ? TONE_CLASS.legacy
                  : undefined
                : state.kind === "failed" || state.kind === "judge_failed"
                  ? TONE_CLASS.failed
                  : TONE_CLASS.na,
            )}
            style={state.kind === "scored" && !state.tag ? scoreColor(state.score) : undefined}
          >
            {TAG[state.kind]}
            {state.kind === "scored" && state.tag ? ` · ${state.tag}` : ""}
          </span>
        </div>
        <div className="grid gap-0.5 text-[13px] tabular-nums" title="입력부터 최종 응답 완료까지 · 도구 실행 포함 · 채점 시간 제외">
          <span className="text-[11px] text-muted-foreground">Latency</span>
          <span>{formatLatency(item.status === "done" ? replayLatencyMs(replay) : null)}</span>
        </div>
        {/* The coordinates first, the question under them — artifact. */}
        <div className="grid min-w-0 gap-0.5">
          <div className="text-sm font-semibold">
            TC {tcShort(item.test_case_id)} × {letter}
          </div>
          <div className="truncate text-xs text-muted-foreground" title={query}>
            {query.length > 64 ? `${query.slice(0, 64)}…` : query} · {letter} {teName(run.te_spec ?? {}, baseline)}
          </div>
        </div>
        {why && <p className="basis-full text-[13px] text-muted-foreground">{why}</p>}
      </Card>

      {/* Run summary (§4.1 · §4.2) */}
      <Card>
        <CardHead title="실행 요약" hint={runLabel} />
        {trace && (
          <TraceMetricsRow metrics={trace.metrics} className="mt-0 mb-0 border-b border-border px-4 py-3">
            {code && text(code.sha) && <Badge>commit {shortSha(text(code.sha))}</Badge>}
          </TraceMetricsRow>
        )}
        <dl className="grid grid-cols-[112px_minmax(0,1fr)] gap-x-3 gap-y-2 px-4 py-3 text-[13px]">
          <Term>실행 시각</Term>
          <dd>{compactTime(item.finished_at ?? replay?.completed_at ?? run.created_at)}</dd>
          <Term>Batch Test</Term>
          <dd>{detail.page ? `${detail.page.label} · ${detail.page.name}` : "페이지 없이 실행됨"}</dd>
          <Term>TE</Term>
          <dd>
            {letter} · {teName(run.te_spec ?? {}, baseline)}{" "}
            <span className="text-muted-foreground">{teSub(run.te_spec ?? {}, baseline)}</span>
          </dd>
          <Term>Model</Term>
          <dd>
            {run.axes.llm_model
              ? modelLabel(run.axes.llm_model, baseline)
              : `${modelLabel(baseline?.default_model, baseline)} (배포본)`}
            <span className="text-muted-foreground">
              {" "}
              · tier {run.axes.model_tier ?? "기본"} · effort {run.axes.reasoning_effort ?? "기본"}
            </span>
          </dd>
          <Term>Code</Term>
          <dd>
            {code
              ? `${shortSha(text(code.sha))} ${text(code.subject)} · ${text(code.resolution) || run.axes.code_resolution || "—"}`
              : run.axes.git_ref
                ? `${shortSha(run.axes.git_ref)} · ${run.axes.code_resolution ?? "—"}`
                : "배포본"}
          </dd>
          <Term>재현 입력</Term>
          <dd>
            history_limit {run.axes.history_limit ?? baseline?.history_limit ?? 50} · force_response{" "}
            {String(run.axes.force_response ?? baseline?.force_response ?? false)}
          </dd>
          <Term>snapshot_at</Term>
          <dd>{compactTime(run.axes.snapshot_at ?? manifest?.snapshot_at)}</dd>
          {/* A result label, not a run condition — prompts follow snapshot_at (§4.2). */}
          <Term>프롬프트</Term>
          <dd>{promptVersions(manifest) ?? <span className="text-muted-foreground">기록 없음</span>}</dd>
          <Term>재현한 👎</Term>
          <dd className="break-all">{item.feedback_id ?? "—"}</dd>
        </dl>
      </Card>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        {/* Evaluation (§4.3 · §4.4) */}
        <Card className="min-w-0">
          <CardHead
            title="Evaluation"
            hint={v2 ? "rubric v2 · 기준 충족도" : v1 ? "rubric v1 · 읽기 전용" : `rubric ${rubric ?? "—"}`}
          />
          <dl className="grid grid-cols-[112px_minmax(0,1fr)] gap-x-3 gap-y-2 px-4 py-3 text-[13px]">
            <Term>{v1 ? "Score" : "Final"}</Term>
            <dd>
              {state.kind === "scored" ? (
                <>
                  <span className="tabular-nums">{formatScore(state.score)}</span>
                  {v1 && v1.axes.length > 0 && (
                    <span className="text-muted-foreground">
                      {" "}
                      = 축 {v1.axes.map((a) => a.score).join(" + ")} / {v1.axes.length}
                    </span>
                  )}
                  <span className="text-muted-foreground"> · scored{state.tag ? ` · ${state.tag}` : ""}</span>
                </>
              ) : (
                <span className="text-muted-foreground">— · {TAG[state.kind]}</span>
              )}
            </dd>
            {v2 && (
              <>
                <Term>Answer Quality</Term>
                <dd className="tabular-nums">{scoreText(v2.scores.answerQuality)}</dd>
                <Term>Execution</Term>
                <dd className="tabular-nums">
                  {v2.scores.execution == null ? (
                    <span className="text-muted-foreground">N/A — 실행 점수를 내지 않았습니다</span>
                  ) : (
                    formatScore(v2.scores.execution)
                  )}
                </dd>
                <Term>계산</Term>
                <dd>
                  <FinalFormula judgement={v2} />
                </dd>
                <Term>평가 정책</Term>
                <dd>
                  <Badge size="compact">{v2.policy ?? "unknown"}</Badge>
                  {v2.policy !== "current" && (
                    <span className="text-muted-foreground"> · 집계에서 뺍니다 (R3.9)</span>
                  )}
                </dd>
              </>
            )}
            <Term>Rubric</Term>
            <dd>
              {rubric ?? "—"}
              <span className="text-muted-foreground">
                {v2
                  ? " · 네 그룹 · 기준 충족도 · 0~1"
                  : v1
                    ? " · 3 축 × 1~5 점 · 읽기 전용 — v2 와 비교하지 않는다"
                    : ""}
              </span>
            </dd>
            <Term>Reference</Term>
            <dd>
              {reference ? (
                <>
                  v{reference.version} · {reference.provider}/{reference.model_name}
                  {reference.model_build ? ` (${reference.model_build})` : ""}
                  <span className="text-muted-foreground"> · 승인 {reference.approved_by ?? "대기"}</span>
                </>
              ) : detail.active_reference ? (
                <span className="text-muted-foreground">
                  채점에 쓰인 것 없음 · 현재 v{detail.active_reference.version}
                </span>
              ) : (
                <span className="text-muted-foreground">없음</span>
              )}
            </dd>
            <Term>Judge</Term>
            <dd>{judgement?.judgeModel ?? <span className="text-muted-foreground">—</span>}</dd>
          </dl>

          {v2 ? (
            <GroupTable groups={v2.groups} />
          ) : v1 ? (
            <V1Axes judgement={v1} />
          ) : (
            <p className="border-t border-border px-4 py-3 text-[13px] text-muted-foreground">
              기준별 판정이 없습니다 — 채점된 실행에만 있습니다.
            </p>
          )}

          {reference && (
            <Disclosure title="Reference 답변" subtitle={`v${reference.version}`} defaultOpen={false}>
              <p className="px-4 pb-3 text-[13px] whitespace-pre-wrap">{reference.answer_body}</p>
            </Disclosure>
          )}
        </Card>

        {/* Actual answer (§4.5) */}
        <Card className="min-w-0">
          <CardHead title="Actual Answer" hint="replay answer" />
          <div className="grid gap-3 p-4">
            <Block label="assistant">{answerText(replay) ?? "실행이 답변에 도달하지 못했습니다."}</Block>
            <Block label="replay trigger" tone="warn">
              {query}
            </Block>
          </div>
        </Card>
      </div>

      {/* Per-criterion verdicts (§4.4 · R9.4.2) — full width: seven columns. */}
      {v2 && <CriteriaCard judgement={v2} />}

      {/* Trace (§5) */}
      <Card className="min-w-0 overflow-hidden">
        <CardHead title="Trace" hint="waterfall · 공유 시간축" />
        <div className="p-4">
          {trace ? (
            <TraceView
              trace={trace}
              title={null}
              showMetrics={false}
              className="mt-0 border-t-0 pt-0"
              emptyNotice="이 실행의 스냅샷에 턴 기록이 없습니다."
            />
          ) : (
            <p className="text-[13px] text-muted-foreground">
              실행이 답변에 도달하지 못해 저장된 트레이스가 없습니다 — Replay Lab 은 성공한 실행만 기록합니다.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}

function rubricOf(evaluation: Record<string, unknown> | null): string | null {
  return typeof evaluation?.rubric_version === "string" ? evaluation.rubric_version : null;
}

function answerText(replay: Record<string, unknown> | null): string | null {
  if (!replay || !isRecord(replay.assistant_message)) return null;
  const message = replay.assistant_message;
  const content = text(message.content);
  if (content) return content;
  const meta = isRecord(message.metadata) ? message.metadata : null;
  const failure = meta && isRecord(meta.error) ? text(meta.error.message) : "";
  return failure ? `답변 본문이 비어 있습니다 — ${failure}` : "답변 본문이 비어 있습니다.";
}

/** `base_prompt v12 · tool_guideline v4 …` from the manifest, if it has one. */
function promptVersions(manifest: Record<string, unknown> | null): string | null {
  const snapshot = manifest && isRecord(manifest.prompt_snapshot) ? manifest.prompt_snapshot : null;
  const tables = snapshot && isRecord(snapshot.tables) ? snapshot.tables : null;
  if (!tables) return null;
  const parts: string[] = [];
  for (const [name, rows] of Object.entries(tables)) {
    if (!Array.isArray(rows)) continue;
    const versions = [
      ...new Set(rows.filter(isRecord).map((r) => r.version).filter((v) => v != null).map(String)),
    ];
    if (versions.length) parts.push(`${name} v${versions.join("/")}`);
  }
  return parts.length ? parts.join(" · ") : null;
}

function Term({ children }: { children: React.ReactNode }) {
  return <dt className="pt-0.5 text-[11px] tracking-wide text-muted-foreground uppercase">{children}</dt>;
}

function Block({
  label,
  tone,
  children,
}: {
  label: string;
  tone?: "warn";
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-surface-subtle p-3",
        tone === "warn" ? "border-l-2 border-l-warning" : "border-l-2 border-l-primary",
      )}
    >
      <span className="mb-1 block text-[11px] tracking-widest text-muted-foreground uppercase">{label}</span>
      <p className="text-[14px] whitespace-pre-wrap">{children}</p>
    </div>
  );
}
